import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

/**
 * Badge — Anthropic Claude Peach uslubidagi semantic pill.
 * R4 polish: warm-tone semantic ranglar, pill rounded-full, dot 6px optional.
 *
 * Variantlar:
 *   - default/brand/success/warning/danger/info — semantic warm-tone
 *   - muted — soft yashirin
 *   - aurora — backward compat (brand kabi ishlaydi)
 *   - role-* — OWNER/ADMIN/LEAD/MEMBER (tokens.css)
 *   - plan-* — FREE/PRO/ENTERPRISE
 *   - status-* — PENDING/ACCEPTED/EXPIRED/REVOKED
 *
 * UX:
 *   - Dot indicator (status pill stilida) — `dot` prop bilan, 6px
 *   - Solid border + soft bg (Vercel/Linear stilida)
 */
const badgeVariants = cva(
  cn(
    "inline-flex items-center gap-1.5 rounded-full whitespace-nowrap",
    "border font-medium leading-none",
  ),
  {
    variants: {
      variant: {
        default: cn(
          "bg-[var(--bg-elevated)] text-[var(--text-primary)]",
          "border-[var(--border-strong)]",
        ),
        muted: cn(
          "bg-[var(--bg-surface)] text-[var(--text-secondary)]",
          "border-[var(--border-subtle)]",
        ),
        // Brand — Claude Peach soft pill
        brand: cn(
          "bg-[var(--brand-soft)] text-[var(--brand-peach)]",
          "border-[var(--brand-primary)]/40",
        ),
        success: cn(
          "bg-[var(--success-soft)] text-[var(--success)]",
          "border-[var(--success)]/40",
        ),
        warning: cn(
          "bg-[var(--warning-soft)] text-[var(--warning)]",
          "border-[var(--warning)]/40",
        ),
        danger: cn(
          "bg-[var(--danger-soft)] text-[var(--danger)]",
          "border-[var(--danger)]/40",
        ),
        info: cn(
          "bg-[var(--info-soft)] text-[var(--info)]",
          "border-[var(--info)]/40",
        ),
        // Backward compat — eski "aurora" variant brand kabi
        aurora: cn(
          "bg-[var(--brand-soft)] text-[var(--brand-peach)]",
          "border-[var(--brand-primary)]/40",
        ),

        // Role badges (tokens.css)
        "role-owner": cn(
          "bg-[var(--role-owner-bg)] text-[var(--role-owner-fg)]",
          "border-[var(--role-owner-border)]",
        ),
        "role-admin": cn(
          "bg-[var(--role-admin-bg)] text-[var(--role-admin-fg)]",
          "border-[var(--role-admin-border)]",
        ),
        "role-lead": cn(
          "bg-[var(--role-lead-bg)] text-[var(--role-lead-fg)]",
          "border-[var(--role-lead-border)]",
        ),
        "role-member": cn(
          "bg-[var(--role-member-bg)] text-[var(--role-member-fg)]",
          "border-[var(--role-member-border)]",
        ),

        // Plan badges
        "plan-free": cn(
          "bg-[var(--plan-free-bg)] text-[var(--plan-free-fg)]",
          "border-[var(--plan-free-border)]",
        ),
        "plan-pro": cn(
          "bg-[var(--plan-pro-bg)] text-[var(--plan-pro-fg)]",
          "border-[var(--plan-pro-border)]",
        ),
        "plan-enterprise": cn(
          "bg-[var(--plan-enterprise-bg)] text-[var(--plan-enterprise-fg)]",
          "border-[var(--plan-enterprise-border)]",
        ),

        // Invitation status
        "status-pending": cn(
          "bg-[var(--status-pending-bg)] text-[var(--status-pending-fg)]",
          "border-[var(--status-pending-border)]",
        ),
        "status-accepted": cn(
          "bg-[var(--status-accepted-bg)] text-[var(--status-accepted-fg)]",
          "border-[var(--status-accepted-border)]",
        ),
        "status-expired": cn(
          "bg-[var(--status-expired-bg)] text-[var(--status-expired-fg)]",
          "border-[var(--status-expired-border)]",
        ),
        "status-revoked": cn(
          "bg-[var(--status-revoked-bg)] text-[var(--status-revoked-fg)]",
          "border-[var(--status-revoked-border)]",
        ),
      },
      size: {
        sm: "h-4 px-1.5 text-[10px]",
        md: "h-5 px-2 text-[11px]",
        lg: "h-6 px-2.5 text-xs",
      },
    },
    defaultVariants: { variant: "default", size: "md" },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Old indicator dot (status pill style) — 6px */
  dot?: boolean;
  /** Coral pulse animation (active tenant ko'rsatkich) */
  pulse?: boolean;
}

const dotColorByVariant: Partial<Record<NonNullable<BadgeProps["variant"]>, string>> = {
  brand: "bg-[var(--brand-primary)]",
  success: "bg-[var(--success)]",
  warning: "bg-[var(--warning)]",
  danger: "bg-[var(--danger)]",
  info: "bg-[var(--info)]",
  aurora: "bg-[var(--brand-primary)]",
  "status-pending": "bg-[var(--warning)]",
  "status-accepted": "bg-[var(--success)]",
  "status-expired": "bg-[var(--text-muted)]",
  "status-revoked": "bg-[var(--danger)]",
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, size, dot, pulse, children, ...props }, ref) => {
    const dotClass = dotColorByVariant[variant ?? "default"] ?? "bg-current";
    return (
      <span
        ref={ref}
        className={cn(badgeVariants({ variant, size }), className)}
        {...props}
      >
        {dot && (
          <span
            aria-hidden
            className={cn(
              // 6px optional dot (R4 spec)
              "inline-block h-1.5 w-1.5 rounded-full",
              dotClass,
              pulse && "animate-pulse",
            )}
          />
        )}
        {children}
      </span>
    );
  },
);
Badge.displayName = "Badge";
